import { useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Button, Alert, Chip, Grid2 as Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxOutsourcedTestDto } from "@/api/diagnostics";
import { vendorsApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { LabTestPicker } from "@/components/LabTestPicker";
import { DiagnosticOrderIdField } from "@/components/DiagnosticOrderIdField";

const statusColor = (s: string) => s === "ResultReceived" ? "success" : s === "Dispatched" ? "info" : s === "Cancelled" ? "default" : "warning";

export function DiagnosticsOutsourcedPage() {
  const canManage = useAuth(s => s.hasPermission("diagnostics.outsourced.manage"));
  const canImport = useAuth(s => s.hasPermission("diagnostics.outsourced.import"));
  const qc = useQueryClient();

  const vendors = useQuery({ queryKey: ["vendors-all"], queryFn: () => vendorsApi.list({ pageSize: 200, isActive: true }) });
  const tests = useQuery({ queryKey: ["dx-outsourced-tests"], queryFn: () => diagnosticsApi.listOutsourcedTests() });
  const mappings = useQuery({ queryKey: ["dx-outsource-mappings"], queryFn: () => diagnosticsApi.listOutsourceMappings() });
  const perf = useQuery({ queryKey: ["dx-vendor-perf"], queryFn: () => diagnosticsApi.vendorPerformance() });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["dx-outsourced-tests"] }); qc.invalidateQueries({ queryKey: ["dx-vendor-perf"] }); };

  // mapping
  const [mTest, setMTest] = useState(""); const [mVendor, setMVendor] = useState(""); const [mCost, setMCost] = useState(""); const [mTat, setMTat] = useState("");
  const addMapping = useMutation({
    meta: { successMessage: "Vendor mapping added" },
    mutationFn: () => diagnosticsApi.createOutsourceMapping({ labTestId: mTest, vendorId: mVendor, cost: mCost === "" ? null : Number(mCost), tatDays: mTat === "" ? null : Number(mTat) }),
    onSuccess: () => { setMTest(""); setMVendor(""); setMCost(""); setMTat(""); qc.invalidateQueries({ queryKey: ["dx-outsource-mappings"] }); },
  });

  // dispatch a test
  const [tOrder, setTOrder] = useState(""); const [tTest, setTTest] = useState(""); const [tVendor, setTVendor] = useState("");
  const createTest = useMutation({
    meta: { successMessage: "Outsourced test created" },
    mutationFn: () => diagnosticsApi.createOutsourcedTest({ diagnosticOrderId: tOrder, labTestId: tTest, vendorId: tVendor }),
    onSuccess: () => { setTOrder(""); setTTest(""); setTVendor(""); refresh(); },
  });

  const dispatch = useMutation({ meta: { successMessage: "Dispatched" }, mutationFn: (id: string) => diagnosticsApi.dispatchOutsourcedTest(id, {}), onSuccess: refresh });
  const receive = useMutation({ meta: { successMessage: "Result imported" }, mutationFn: (vars: { id: string; value: string }) => diagnosticsApi.receiveOutsourcedResult(vars.id, { resultValue: vars.value }), onSuccess: refresh });
  const cancel = useMutation({ meta: { successMessage: "Cancelled" }, mutationFn: (id: string) => diagnosticsApi.cancelOutsourcedTest(id), onSuccess: refresh });

  const columns: GridColDef<DxOutsourcedTestDto>[] = [
    { field: "vendorName", headerName: "Vendor", width: 160 },
    { field: "labTestId", headerName: "Test", width: 130, valueGetter: v => (v as string)?.slice(0, 8) },
    { field: "status", headerName: "Status", width: 130, renderCell: p => <Chip size="small" label={p.row.status} color={statusColor(p.row.status)} variant="outlined" /> },
    { field: "cost", headerName: "Cost", width: 90, align: "right", headerAlign: "right" },
    { field: "resultValue", headerName: "Result", flex: 1, minWidth: 140 },
    {
      field: "actions", headerName: "", width: 230, sortable: false, renderCell: p => (
        <Stack direction="row" spacing={0.5}>
          {p.row.status === "Pending" && canManage && <Button size="small" disabled={dispatch.isPending} onClick={() => dispatch.mutate(p.row.id)}>Dispatch</Button>}
          {p.row.status === "Dispatched" && canImport && <Button size="small" onClick={() => { const v = window.prompt("Result value"); if (v) receive.mutate({ id: p.row.id, value: v }); }}>Import</Button>}
          {p.row.status !== "ResultReceived" && p.row.status !== "Cancelled" && canManage && <Button size="small" color="error" disabled={cancel.isPending} onClick={() => cancel.mutate(p.row.id)}>Cancel</Button>}
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Outsourced tests" />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Vendor mapping</Typography>
            <Stack spacing={1}>
              <LabTestPicker value={mTest} onChange={setMTest} required />
              <Stack direction="row" spacing={1}>
                <TextField select label="Vendor" size="small" value={mVendor} onChange={e => setMVendor(e.target.value)} sx={{ flexGrow: 1 }}>
                  {(vendors.data?.items ?? []).map(v => <MenuItem key={v.id} value={v.id}>{v.code} — {v.name}</MenuItem>)}
                </TextField>
                <TextField label="Cost" size="small" type="number" value={mCost} onChange={e => setMCost(e.target.value)} sx={{ width: 100 }} />
                <TextField label="TAT (days)" size="small" type="number" value={mTat} onChange={e => setMTat(e.target.value)} sx={{ width: 110 }} />
              </Stack>
              {addMapping.isError && <Alert severity="error">{errorMessage(addMapping.error)}</Alert>}
              {canManage && <Button variant="outlined" disabled={!mTest || !mVendor || addMapping.isPending} onClick={() => addMapping.mutate()} sx={{ alignSelf: "flex-start" }}>Add mapping</Button>}
            </Stack>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow><TableCell>Vendor</TableCell><TableCell>Cost</TableCell><TableCell>TAT</TableCell></TableRow></TableHead>
              <TableBody>{(mappings.data ?? []).map(m => <TableRow key={m.id}><TableCell>{m.vendorName}</TableCell><TableCell>{m.cost}</TableCell><TableCell>{m.tatDays}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Send test to external lab</Typography>
            <Stack spacing={1}>
              <DiagnosticOrderIdField value={tOrder} onChange={setTOrder} required />
              <LabTestPicker value={tTest} onChange={setTTest} required />
              <TextField select label="Vendor" size="small" value={tVendor} onChange={e => setTVendor(e.target.value)}>
                {(vendors.data?.items ?? []).map(v => <MenuItem key={v.id} value={v.id}>{v.code} — {v.name}</MenuItem>)}
              </TextField>
              {createTest.isError && <Alert severity="error">{errorMessage(createTest.error)}</Alert>}
              {canManage && <Button variant="contained" disabled={!tOrder || !tTest || !tVendor || createTest.isPending} onClick={() => createTest.mutate()} sx={{ alignSelf: "flex-start" }}>Create</Button>}
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Vendor performance</Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Vendor</TableCell><TableCell>Dispatched</TableCell><TableCell>Completed</TableCell><TableCell>Avg TAT (d)</TableCell><TableCell>Cost</TableCell></TableRow></TableHead>
              <TableBody>{(perf.data ?? []).map(v => <TableRow key={v.vendorId}><TableCell>{v.vendorName}</TableCell><TableCell>{v.dispatched}</TableCell><TableCell>{v.completed}</TableCell><TableCell>{v.avgTatDays}</TableCell><TableCell>{v.totalCost}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Outsourced tests</Typography>
            {tests.isError && <Alert severity="error" sx={{ mb: 1 }}>{errorMessage(tests.error)}</Alert>}
            <Box sx={{ height: 380 }}>
              <DataGrid rows={tests.data ?? []} columns={columns} loading={tests.isLoading} getRowId={r => r.id}
                disableRowSelectionOnClick pageSizeOptions={[25]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
